import { useNavigate } from "react-router-dom";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import classNames from "classnames";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import Button from "../Button";
import { FormLabel } from "../Input/FormLabel";

type InputSize = "lg" | "md" | "sm";

interface IconObject {
  left?: React.ReactNode;
  right?: React.ReactNode;
}

export interface DropdownItem {
  _id?: string;
  id?: string;
  name?: string;
  title?:
    | React.ReactNode
    | ((args: { item: DropdownItem; active: boolean }) => React.ReactNode);
  label?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  path?: string;
  onClick?: (item: DropdownItem) => void;
  // Allow additional fields commonly passed through
  [key: string]: unknown;
}

interface InputDropdownProps {
  data: DropdownItem[];
  placeholder?: string;
  size?: InputSize;
  className?: string;
  wrapperClasses?: string;
  dataWrapperClassName?: string;
  label?: React.ReactNode;
  labelClasses?: string;
  id?: string;
  required?: boolean;
  icon?: React.ReactElement | IconObject;
  onChange?: (value: string) => void;
  footerButton?: React.ReactNode;
  footerButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  renderItem?: (args: {
    selected: boolean;
    active: boolean;
    item: DropdownItem;
  }) => React.ReactNode;
}

export const InputDropdown: React.FC<InputDropdownProps> = (props) => {
  const [data, setData] = useState<DropdownItem[]>(() => props.data);
  const [selected, setSelected] = useState<DropdownItem | null>(() => null);
  const [query, setQuery] = useState<string>("");
  const [showError] = useState(false);

  const filteredData: DropdownItem[] =
    query === ""
      ? data
      : data.filter((item) =>
          (item.name ?? "")
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const navigate = useNavigate();

  useEffect(() => {
    if (props.data) {
      setData(props.data);
    }
  }, [data.length, props.data.length]);

  function handleClick(item: DropdownItem) {
    item.onClick ? item.onClick(item) : item.path && navigate(item.path);
  }

  function renderItem({
    item,
    active,
    selected,
  }: {
    item: DropdownItem;
    active: boolean;
    selected: boolean;
  }) {
    const title = item.title || item.label;
    const itemClasses = classNames(
      "group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer",
      {
        "bg-primary-600 text-white": active,
        "text-gray-900": !active,
        "text-gray-100 !cursor-not-allowed": item.disabled,
      }
    );
    return props.renderItem ? (
      props.renderItem({ selected, active, item })
    ) : (
      <div
        onClick={item.disabled ? undefined : () => handleClick(item)}
        className={itemClasses}
      >
        <span className="mr-1.5">{item.icon}</span>
        {typeof title === "function" ? title({ item, active }) : title}
      </div>
    );
  }

  const labelClasses = classNames(
    "block mb-1",
    "text-sm font-medium",
    props.labelClasses,
    {
      "text-gray-900": !showError,
      "text-red-700": showError,
    }
  );

  const dataWrapperClasses = classNames(
    "absolute mt-2 w-56 rounded-md w-full z-10",
    "bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
    props.dataWrapperClassName
  );

  const sizeLg = props.size === "lg";
  const sizeMd = props.size === "md" || !props.size;
  const sizeSm = props.size === "sm";

  const isIconReactElement = props.icon
    ? React.isValidElement(props.icon)
    : false;

  const classes = classNames(
    "shadow-sm bg-white",
    "block w-full focus:outline-none focus-visible:ring-1 focus:ring-1 focus-visible:ring-primary-500",
    "border rounded-lg bg-transparent",
    { "p-4 text-md": sizeLg },
    { "p-2.5 text-sm": sizeMd },
    { "p-2 text-xs": sizeSm },
    {
      "border-gray-300 focus:ring-primary-500 focus:border-primary-500 text-gray-900":
        !showError,
    },
    {
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:outline-2 focus:ring-red-500 focus:border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500":
        showError,
    },
    {
      "pl-8": !!(
        props.icon &&
        !isIconReactElement &&
        (props.icon as IconObject).left
      ),
    },
    props.className
  );

  const wrapperClasses = classNames("block", props.wrapperClasses);

  return (
    <div className="w-full relative z-1000">
      {props.label && (
        <FormLabel
          htmlFor={props.id}
          required={props.required}
          className={labelClasses}
        >
          {props.label}
        </FormLabel>
      )}
      <Combobox value={selected} onChange={setSelected}>
        <div className="relative mt-1">
          <div className={wrapperClasses}>
            <ComboboxInput
              placeholder={props.placeholder}
              className={classes}
              displayValue={(item: DropdownItem) => item?.name || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                props.onChange
                  ? props.onChange(event.target.value)
                  : setQuery(event.target.value)
              }
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <ComboboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {filteredData.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredData.map((item) => (
                  <ComboboxOption
                    key={(item._id as string) || (item.id as string)}
                    className={({ active }) =>
                      `relative cursor-default select-none py-1 px-3 ${
                        active ? "bg-teal-600 text-white" : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>{renderItem({ selected, active, item })}</>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>
      {props.footerButton && (
        <div className="flex justify-start mt-2">
          <Button type="link" onClick={props.footerButtonClick}>
            {props.footerButton}
          </Button>
        </div>
      )}
    </div>
  );
};

export default InputDropdown;
